import type { SupporterLoginReturnStatus } from "@/features/supporter/model/supporterReturn";
import {
  createMediator,
  type Mediator,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export interface SupporterReturnState {
  readonly processed: boolean;
}

export type SupporterReturnEvent = {
  readonly type: "supporter-return/detected";
  readonly status: SupporterLoginReturnStatus | null;
  readonly cleanUrl: string;
  readonly isDesktop: boolean;
  readonly isSettingsRoute: boolean;
};

export type SupporterReturnEffect =
  | { readonly type: "supporter-return/clean-url"; readonly url: string }
  | {
      readonly type: "supporter-return/set-login-return";
      readonly status: SupporterLoginReturnStatus;
    }
  | { readonly type: "supporter-return/refresh-session" }
  | {
      readonly type: "supporter-return/navigate-to-settings";
      readonly replace: boolean;
    };

export function isSupporterReturnEvent(
  event: TypedEvent,
): event is SupporterReturnEvent {
  return event.type === "supporter-return/detected";
}

export function createSupporterReturnMediator(
  runEffect: (effect: SupporterReturnEffect) => void,
  restoredState?: SupporterReturnState,
): Mediator<SupporterReturnState, SupporterReturnEvent, SupporterReturnEffect> {
  return createMediator<
    SupporterReturnState,
    SupporterReturnEvent,
    SupporterReturnEffect
  >(
    restoredState ?? { processed: false },
    (state, event) => {
      if (state.processed) return { status: "handled", state };

      const effects: SupporterReturnEffect[] = [
        { type: "supporter-return/clean-url", url: event.cleanUrl },
      ];
      if (event.status !== null) {
        effects.push({
          type: "supporter-return/set-login-return",
          status: event.status,
        });
        if (event.status === "connected") {
          effects.push({ type: "supporter-return/refresh-session" });
        }
        if (!event.isDesktop) {
          effects.push({
            type: "supporter-return/navigate-to-settings",
            replace: event.isSettingsRoute,
          });
        }
      }

      return {
        status: "handled",
        state: { processed: true },
        effects,
      };
    },
    { runEffect },
  );
}
